import React from 'react';
import { Link } from 'react-router-dom';
import useReveal from '../hooks/useReveal';
import SplitCompare from '../components/SplitCompare';
import SEO from '../components/SEO';
// ── UPWORK REVIEWS ───────────────────────────────────────────────────
const REVIEWS = {
  '01': {
    name:   'Client — Upwork',
    title:  'AI Invoice Processing Project',
    rating: 5,
    date:   'Mar 13 – Mar 23, 2026',
    
    text:   "Worked with Rohit and his agency! They have detailed knowledge of the work they do. Always got a perfect outcome and always ready to fix and explain things if something you don't understand. Rohit has good understanding of Software Engineering and AI Enterprise solutions.",
  },
  '02': {
    name:   'Client — Upwork',
    title:  'Django Knowledge Management System',
    rating: 5,
    date:   'Aug 13 – Aug 18, 2026',
    
  },
  '04': {
    name:   'Client — Upwork',
    title:  'Backend API Integration & LLM Deployment',
    rating: 5,
    date:   'Oct 13 – Oct 28, 2025',
    
    text:   "The team did an exceptional job integrating a large language model with our existing API. Their technical expertise, communication, and professionalism stood out from the very beginning. They not only understood the requirements clearly but also provided valuable suggestions that improved the overall design and scalability of our backend. What impressed us most was their ability to deliver high-quality work ahead of schedule while maintaining clean, well-documented code. I'd highly recommend Arhat Info for any future backend or AI integration projects — a true expert and a pleasure to work with.",
  },
  '05': {
    name:   'Client — Upwork',
    title:  'React Chatbot with LLM Integration',
    rating: 5,
    date:   'Aug 27 – Oct 10, 2025',
    
    text:   "Rohit delivered good work on this React LLM development project and I enjoyed working with him. His communication was top-notch, he met all deadlines, and his skills were reasonably strong. At one point I asked for an additional milestone and he was very forthcoming that the additional work was outside his area of expertise. He helped me find an additional team member on this project to support the work. I enjoyed working with Rohit and will likely have additional jobs for him in the future.",
  },
};

// ── CASE STUDIES ─────────────────────────────────────────────────────
const CASES = [
  {
    n:'01',
    title:'AI Invoice Processing Automation',
    subtitle:'Finance Automation · AI Document Processing · Python',
    prob:'A growing business was manually processing 200+ invoices weekly. Finance staff spent 15+ hours reviewing PDFs, copying data into spreadsheets — all prone to human error and bottlenecks at month-end.',
    solution:'We built a fully automated AI pipeline that ingests PDF invoices, extracts all key fields using OCR and structured LLM extraction, validates against business rules, and pushes clean data directly to the accounting system. The system handles vendor name, amount, date, and line-item extraction with confidence scoring and a human-in-the-loop review queue for edge cases.',
    flow:['Client Uploads PDF','OCR Extracts Data','AI Identifies Fields','Vendor · Amount · Date','Pushed to Accounting System'],
    tags:['Python','OCR','OpenAI','FastAPI','PostgreSQL','AWS S3'],
    sector:'Finance / Operations',
    arch:'Async pipeline · REST API · PostgreSQL + S3',
    feats:['Vendor, date, amount, line-item extraction','AI field identification with confidence scoring','Human-in-loop review queue for edge cases','Webhook push to accounting system','Audit trail and full processing history'],
    impact:'Reduced manual processing time by 85%. Zero missed fields on invoices scoring above 90% confidence. Finance team freed to review exceptions only.',
  },
  {
    n:'02',
    title:'Django Knowledge Management System',
    subtitle:'Internal Tools · AI Q&A · Django · OpenAI',
    prob:'A mid-size company had hundreds of internal policy docs, procedure guides, and SOPs scattered across folders. Employees spent significant time searching for answers — often finding outdated information.',
    solution:'We built a Django-powered knowledgebase with semantic search and OpenAI Q&A integration. Staff type natural-language questions; the system retrieves relevant documents and generates precise, sourced answers with admin controls to update content without engineering involvement.',
    flow:['User Query','Semantic Search','Context Retrieval','OpenAI LLM','Sourced Answer'],
    tags:['Django','PostgreSQL','OpenAI','pgvector','AWS EC2','RDS'],
    sector:'Enterprise Internal Tools',
    arch:'Django REST · pgvector · OpenAI RAG · AWS EC2 + RDS',
    feats:['Natural language Q&A over internal docs','Semantic similarity search with pgvector','Source citations with every answer','Django admin for content management','User auth and department-level access'],
    impact:'Eliminated repetitive support requests. Knowledge retrieval dropped from minutes to seconds. Admin team can update docs without engineering involvement.',
  },
  {
    n:'03',
    title:'Solar Simulation & Optimization Backend',
    subtitle:'Energy Tech · Large-Scale Simulation · AWS',
    prob:"Solar engineering teams needed to run performance simulations and shade analyses across 100+ sites simultaneously. Existing processes were manual and couldn't scale to ~100MW project volume.",
    solution:'We built a distributed simulation backend on AWS — ingesting site data, running performance and shade analysis pipelines in parallel, integrating NASA and NREL weather APIs, and generating engineering file exports (DXF, KMZ, PVSyst SHD) on demand.',
    flow:['Site Data','Weather APIs','Simulation Engine','Shade Analysis','File Export'],
    tags:['Python','AWS Lambda','DynamoDB','Redis','S3','NASA API','NREL'],
    sector:'Solar & Energy Technology',
    arch:'AWS Lambda · DynamoDB · Redis queue · S3 exports',
    feats:['Parallel simulation across 100+ sites','NASA/NREL weather data integration','Environmental shade analysis pipelines','DXF, KMZ, PVSyst SHD file generation','Tracker optimization support workflows'],
    impact:'Simulation time cut from days to hours. Engineering teams could run full site analysis on demand — supporting ~100MW of solar projects at scale.',
  },
  {
    n:'04',
    title:'LLM Backend API Integration',
    subtitle:'API Development · OpenAI Integration · Scalability',
    prob:'An existing SaaS product needed AI capabilities layered in without a full rewrite. Previous integrations caused latency spikes, inconsistent outputs, and unpredictable API costs that spiraled out of control.',
    solution:'We designed a clean AI service layer alongside the existing backend — handling prompt construction, response caching, structured output parsing, and token cost management. The integration is testable, monitorable, and easy to extend.',
    flow:['Client App','AI Service Layer','Prompt Engine','OpenAI API','Structured Output'],
    tags:['Python','REST APIs','OpenAI','Redis Cache','Structured Output'],
    sector:'SaaS / AI Product',
    arch:'Modular AI service · Redis caching · Production endpoints',
    feats:['Pluggable AI layer into existing backend','Structured JSON output with schema validation','Response caching for cost reduction','Token usage monitoring and alerting','Fallback handling and retry logic'],
    impact:'40% reduction in API costs via caching. Latency stabilized under 800ms p95. Team can now extend AI features without touching core infrastructure.',
  },
  {
    n:'05',
    title:'React Chatbot with LLM Integration',
    subtitle:'React · LLM Integration · Chatbot Deployment',
    prob:'A client needed a production-ready AI chatbot embedded into their React application, integrated with a Large Language Model. Previous attempts had failed due to poor deployment pipelines, broken LLM context handling, and weak user experience.',
    solution:'We built and deployed a full React chatbot UI integrated with an LLM backend — handling conversation context, streaming responses, session management, and smooth deployment into the client\'s existing system. We also identified scope gaps early and helped the client extend their team with the right specialist for out-of-scope work.',
    flow:['User Message','React Chat UI','LLM API Call','Streaming Response','Conversation Context'],
    tags:['React','LLM Integration','Python','Streaming API','Deployment','Testing'],
    sector:'SaaS / AI Product',
    arch:'React frontend · LLM backend · Streaming responses · Session management',
    feats:['React chatbot UI with streaming message display','LLM context window and conversation management','Functionality testing and QA across devices','Integration with client\'s existing systems','Clean deployment pipeline with rollback support'],
    impact:'Delivered ahead of schedule with all milestones met. Client noted exceptional communication and plans to bring additional projects to the team.',
  },
];

// ── STARS ────────────────────────────────────────────────────────────
function Stars({ rating = 5 }) {
  return (
    <div style={{ display:'flex', gap:2, alignItems:'center' }}>
      {[1,2,3,4,5].map(i => (
        <svg key={i} width="13" height="13" viewBox="0 0 24 24"
          fill={i <= rating ? '#35BBEA' : 'rgba(96,165,250,0.2)'}>
          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
        </svg>
      ))}
      <span style={{ fontSize:'.72rem', fontWeight:700, color:'#35BBEA', marginLeft:4 }}>
        {rating}.0
      </span>
    </div>
  );
}

// ── UPWORK LOGO MARK ─────────────────────────────────────────────────
function UpworkMark({ size = 22 }) {
  return (
    <div style={{
      background:'#6FDA44', borderRadius:4,
      width:size, height:size,
      display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0,
    }}>
      <svg width={size * 0.58} height={size * 0.58} viewBox="0 0 40 40" fill="white">
        <path d="M30.2 23.5c-2.3 0-4.5-1-6.3-2.6l.5-2.2.1-.3c.4-2.4 1.8-6.4 5.7-6.4 3 0 5.4 2.4 5.4 5.7s-2.4 5.8-5.4 5.8zm0-15.5c-4.4 0-7.8 2.9-9.2 7.6-.8-2.3-2.7-4.2-3.7-6.3v8.7l-.3 1.3c-.7 3.3-2.8 5.4-5.5 5.4-3 0-5.4-2.5-5.4-5.7s2.4-5.7 5.4-5.7c.5 0 1 .1 1.5.2V7.4C12.3 7.1 11.7 7 11 7 4.9 7 0 11.9 0 18s4.9 11 11 11c5.4 0 9.4-3.7 10.5-9.3.9 2.5 2.7 4.4 4.4 6.3L23 34h6.3l2.3-10.4c.8.1 1.6.2 2.4.2l.6-6c-.7.2-2.3.2-4.4-.8z"/>
      </svg>
    </div>
  );
}

// ── REVIEW CARD ──────────────────────────────────────────────────────
function ReviewCard({ review }) {
  return (
    <div style={{
      marginTop:'1.4rem', padding:'1.4rem',
      background:'rgba(53,187,234,0.05)',
      border:'1px solid rgba(53,187,234,0.22)',
      borderRadius:'var(--r)',
      position:'relative', overflow:'hidden',
    }}>
      <SEO
  title="Case Studies — Backend Engineering & AI Automation Projects | Arhatinfo"
  description="Real project case studies — backend systems, AI automation workflows, SaaS platforms and API integrations delivered for clients worldwide."
  keywords="arhatinfo case studies, backend engineering projects, AI automation case study, SaaS development, Upwork projects, Rohit Jain portfolio"
  canonical="https://arhatinfo.com/case-studies"
  og={{ title:"Arhatinfo Case Studies — Real Engineering Projects", url:"https://arhatinfo.com/case-studies" }}
/>
      {/* top accent */}
      <div style={{
        position:'absolute', top:0, left:0, right:0, height:2,
        background:'linear-gradient(90deg,#35BBEA,#3b82f6)',
      }}/>

      {/* header row */}
      <div style={{
        display:'flex', alignItems:'flex-start',
        justifyContent:'space-between',
        marginBottom:'.9rem', flexWrap:'wrap', gap:'.5rem',
      }}>
        <div style={{ display:'flex', alignItems:'center', gap:'.5rem' }}>
          <UpworkMark size={22} />
          <div>
            <div style={{ fontSize:'.78rem', fontWeight:700, color:'var(--white)', lineHeight:1.2 }}>
              {review.name}
            </div>
            <div style={{ fontSize:'.68rem', color:'var(--muted)', fontWeight:500 }}>
              {review.title}
            </div>
          </div>
        </div>
        <div style={{ display:'flex', flexDirection:'column', alignItems:'flex-end', gap:3 }}>
          <Stars rating={review.rating} />
          <span style={{ fontSize:'.68rem', color:'var(--muted)', fontWeight:500 }}>
            {review.date}
          </span>
        </div>
      </div>

      {/* review text */}
      <p style={{
        fontSize:'.84rem',
        color:'rgba(235,242,255,0.82)',
        lineHeight:1.75,
        fontStyle:'italic',
      }}>
        <span style={{ color:'#35BBEA', fontSize:'1.1rem', fontWeight:700, marginRight:3 }}>"</span>
        {review.text && review.text}
        <span style={{ color:'#35BBEA', fontSize:'1.1rem', fontWeight:700, marginLeft:3 }}>"</span>
      </p>

      {/* budget pill */}
      <div style={{
        marginTop:'.9rem',
        display:'inline-flex', alignItems:'center', gap:6,
        background:'rgba(53,187,234,0.1)',
        border:'1px solid rgba(53,187,234,0.2)',
        borderRadius:20, padding:'3px 10px',
      }}>
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none"
          stroke="#35BBEA" strokeWidth="2.5">
          <path d="M12 1v22M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/>
        </svg>
        {review.budget && <span style={{ fontSize:'.69rem', fontWeight:700, color:'#35BBEA', letterSpacing:'.03em' }}>
          {review.budget}
        </span>}
      </div>
    </div>
  );
}

// ── VERIFIED BADGE ────────────────────────────────────────────────────
function VerifiedBadge() {
  return (
    <div style={{
      marginTop:'1.2rem',
      display:'flex', alignItems:'center', gap:'.6rem',
      padding:'.7rem 1rem',
      background:'rgba(111,218,68,0.07)',
      border:'1px solid rgba(111,218,68,0.2)',
      borderRadius:'var(--rs)',
    }}>
      <UpworkMark size={18} />
      <div>
        <div style={{ fontSize:'.72rem', fontWeight:700, color:'#6FDA44' }}>
          Verified on Upwork
        </div>
        <div style={{ fontSize:'.66rem', color:'var(--muted)', fontWeight:500 }}>
          5.0 ★ · Completed project
        </div>
      </div>
    </div>
  );
}

// ── PAGE ──────────────────────────────────────────────────────────────
export default function CaseStudies() {
  useReveal();
  return (
    <div className="page">
      <div className="ph rv">
        <div className="s-label">Our Work</div>
        <h1>Case Studies</h1>
        <p className="lead">
          Real projects. Real problems. Production-ready solutions built and shipped
          for clients across AI, backend, cloud, and energy sectors.
        </p>
      </div>

      <section className="section" style={{ paddingTop:0 }}>
        <div className="wrap">
          <div className="cs-list">
            {CASES.map((c, i) => (
              <div key={i} className={`cs-card rv d${(i % 2) + 1}`}>

                {/* ── LEFT: main content ── */}
                <div className="cs-main">
                  <div className="cs-num">CASE STUDY {c.n}</div>
                  <h2>{c.title}</h2>
                  <div className="cs-subtitle">{c.subtitle}</div>

                  <div className="cs-block">
                    <div className="cs-label">Problem</div>
                    <p>{c.prob}</p>
                  </div>
                  <div className="cs-block">
                    <div className="cs-label">Solution</div>
                    <p>{c.solution}</p>
                  </div>

                  <div className="cs-label">Pipeline</div>
                  <div className="flow" style={{ marginBottom:'1.1rem' }}>
                    {c.flow.map((f, j) => (
                      <React.Fragment key={f}>
                        <span className="fstep">{f}</span>
                        {j < c.flow.length - 1 && <span className="farr">→</span>}
                      </React.Fragment>
                    ))}
                  </div>

                  <div className="wcard-tags">
                    {c.tags.map(t => <span key={t} className="tag">{t}</span>)}
                  </div>

                  {/* Upwork review if available */}
                  {REVIEWS[c.n] && <ReviewCard review={REVIEWS[c.n]} />}
                </div>

                {/* ── RIGHT: meta panel ── */}
                <div className="cs-meta">
                  <h3 style={{
                    fontFamily:'var(--font)', fontSize:'1rem',
                    fontWeight:700, color:'var(--white)', marginBottom:'1rem',
                  }}>
                    Project Details
                  </h3>
                  <div className="meta-list">
                    <div className="meta-row">
                      <span className="meta-key">Sector</span>
                      <span className="meta-val">{c.sector}</span>
                    </div>
                    <div className="meta-row">
                      <span className="meta-key">Architecture</span>
                      <span className="meta-val">{c.arch}</span>
                    </div>
                    <div className="meta-row">
                      <span className="meta-key">Key Features</span>
                      <ul className="feat-check">
                        {c.feats.map(f => <li key={f}>{f}</li>)}
                      </ul>
                    </div>
                  </div>

                  <div className="impact-box">⚡ {c.impact}</div>

                  {REVIEWS[c.n] && <VerifiedBadge />}
                </div>

              </div>
            ))}
          </div>

          {/* Split compare */}
          <div style={{ marginTop:'4rem' }}>
            <div className="rv" style={{ textAlign:'center', marginBottom:'2rem' }}>
              <div className="s-label">Visual Comparison</div>
              <h2 className="s-title" style={{ fontSize:'clamp(1.8rem,3vw,2.3rem)' }}>
                Before vs. <span className="acc">After Automation</span>
              </h2>
            </div>
            <div style={{ maxWidth:720, margin:'0 auto' }} className="rv d1">
              <SplitCompare />
            </div>
          </div>
        </div>
      </section>

      <section className="section" style={{ paddingTop:'20px' }}>
        <div className="wrap" style={{ textAlign:'center' }}>
          <div className="rv" style={{ maxWidth:560, margin:'0 auto' }}>
            <div className="s-label">Start a Project</div>
            <h2 className="s-title">Have a similar challenge?</h2>
            <p className="s-sub" style={{ margin:'0 auto 2rem' }}>
              We've solved hard backend and AI problems before. Let's talk about yours.
            </p>
            <Link to="/contact" className="btn-p">Book a Free Consultation →</Link>
          </div>
        </div>
      </section>
    </div>
  );
}